import useSWR from 'swr';
import { getSanityClient } from '../../lib/sanity/sanityClient';
import { Otovo$Locale } from '../../types/otovoweb';
import { Sanity$Document } from '../../types/sanityTypes';
import {
  LanguageSwitcherPathResolver,
  LocaleToPathMap,
  Sanity$TranslatedVariant,
} from './types';

function defaultPathResolver(locale: string, slug: string) {
  return slug;
}

function buildLocaleToPathMapForSanityPage(
  defaultLocale: Otovo$Locale,
  translatedVariants: Sanity$TranslatedVariant[],
  pathResolver: (locale: string, slug: string) => string,
): LocaleToPathMap {
  return translatedVariants.reduce((localeToPathMap, translatedVariant) => {
    const locale = translatedVariant.locale
      ? translatedVariant.locale
      : defaultLocale;
    const slug = translatedVariant.slug;
    return {
      ...localeToPathMap,
      [locale]: pathResolver(locale, slug),
    };
  }, {});
}

/**
 * Builds the LocaleToPathMap assuming that the page exists in all
 * market-relevant locales under the same (un-translated) path.
 */
function buildStaticLocaleToPathMap(
  marketRelevantLocales: Otovo$Locale[],
  currentPath: string,
): LocaleToPathMap {
  return marketRelevantLocales.reduce(
    (localeToPathMap, locale) => ({
      ...localeToPathMap,
      [locale]: currentPath,
    }),
    {},
  );
}

type LocaleToPathProps = {
  marketRelevantLocales: Otovo$Locale[];
  defaultLocale: Otovo$Locale;
  currentPath: string;
  sanityDocument?: Sanity$Document;
  pathResolver: LanguageSwitcherPathResolver;
};

export type LocaleToPathReturnType = {
  localeToPathMap: LocaleToPathMap | undefined;
  isLoading: boolean;
  error;
};

function getGroqQuery(sanityDocument: Sanity$Document) {
  return `*[_id == '${sanityDocument._id}']{
    'slug': slug.current,
    '_translations': *[_type == "translation.metadata" && references(^._id)].translations[].value->{
      'slug': slug.current,
      'locale': language,
    }
  }`;
}

/**
 * Collect and provide the data that LanguageSwitcher needs to link alternate
 * language versions of a given page.
 */
export function useLocaleToPathMap({
  marketRelevantLocales,
  defaultLocale,
  currentPath,
  pathResolver = defaultPathResolver,
  sanityDocument,
}: LocaleToPathProps): LocaleToPathReturnType {
  const isCurrentPageFromSanity = !!sanityDocument;

  const {
    data: sanityData,
    error,
    isLoading,
  } = useSWR(
    isCurrentPageFromSanity ? getGroqQuery(sanityDocument) : null,
    (query: string) =>
      getSanityClient({ apiVersion: 'v2025-05-19' }).fetch(query),
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    },
  );

  if (isCurrentPageFromSanity) {
    if (error || !sanityData || !Array.isArray(sanityData)) {
      return { localeToPathMap: undefined, isLoading, error };
    }
    const translations = sanityData[0]._translations;
    const localeToPathMap = buildLocaleToPathMapForSanityPage(
      defaultLocale,
      translations,
      pathResolver,
    );
    return { localeToPathMap, isLoading, error };
  }

  const localeToPathMap = buildStaticLocaleToPathMap(
    marketRelevantLocales,
    currentPath,
  );
  return { localeToPathMap, isLoading, error };
}
